import { ref } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import { useNavigation } from "../use-navigation";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import type { IDomainNewAddress } from "~/domain/new-address";
import type { IDomainNewUncoveredAddress } from "~/domain/new-uncovered-address";
import type { IDomainCoordinate } from "~/domain/coordinate";
import type { IDomainOutletCoverage } from "~/domain/outlet-coverage";
import type { INewAddressEmits } from "~/components/new-address";
import type {
  DeliveryAddress,
  DeliveryLocation,
} from "~/domain/delivery-location";
import { OUTLET_STATES } from "~/domain/outlet-state";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useGuestStore } from "~/stores/guest";
import { useModalHubStore } from "~/components/modal-hub";
import { useDirectBackend } from "#imports";

export const useDelivery = () => {
  const { push } = useNavigation();

  const business = useBusinessStore();
  const basket = useBasketStore();
  const customer = useCustomerStore();
  const guest = useGuestStore();
  const modalHub = useModalHubStore();

  const { businessRepo, outletRepo } = useDirectBackend();

  const isLoading = ref(false);
  const deliveryAddress = ref<IDomainCustomerCoveredAddress>();
  const deliveryLocation = ref<
    IDomainNewAddress | IDomainNewUncoveredAddress
  >();
  const currentGeoLocation = ref<IDomainCoordinate>();

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    country: business.country,
  });

  const onDeliveryLocationSelected = (
    location: IDomainNewAddress,
    skipRefocus = false,
  ) => {
    deliveryAddress.value = undefined;
    deliveryLocation.value = location;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: location.coordinates.lat,
          lng: location.coordinates.lng,
        },
      };
    }
  };

  const onUncoveredDeliveryLocationSelected = (
    location: INewAddressEmits["uncoveredAddressSelected"][0],
    skipRefocus = false,
  ) => {
    deliveryAddress.value = undefined;
    deliveryLocation.value = location;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: location.coordinates.lat,
          lng: location.coordinates.lng,
        },
      };
    }
  };

  const setDeliveryLocationCoordinates = (coordinates: IDomainCoordinate) => {
    if (deliveryLocation.value) {
      deliveryLocation.value = {
        ...deliveryLocation.value,
        coordinates,
      };
    }
  };

  const onAddressSelected = (
    address: IDomainCustomerCoveredAddress,
    skipRefocus = false,
  ) => {
    deliveryLocation.value = undefined;
    deliveryAddress.value = address;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: address.lat,
          lng: address.lng,
        },
      };
    }
  };

  const onRefocusRegion = (region: string) => {
    mapBoundaries.value = {
      location: region,
    };
  };

  const updateMapBoundariesWithGeoLocation = () => {
    if (currentGeoLocation.value) {
      mapBoundaries.value = {
        ...mapBoundaries.value,
        coordinates: currentGeoLocation.value,
      };
    }
  };

  const setCurrentGeoLocation = (coordinate?: IDomainCoordinate) => {
    currentGeoLocation.value = coordinate;
    updateMapBoundariesWithGeoLocation();
  };

  const onDeliverTo = async (
    address: IDomainCustomerCoveredAddress | IDomainNewAddress,
    location: Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">,
  ) => {
    let outletId: string;
    let isPreOrderOnly = false;

    try {
      isLoading.value = true;

      const coverages = await outletRepo.getCoverages({
        businessId: business.id,
        salesChannel: business.salesChannel,
        lat: location.lat,
        lng: location.lng,
      });

      const hasMoreThanOneCoverage = coverages && coverages.length > 1;
      if (hasMoreThanOneCoverage) {
        isLoading.value = false;

        modalHub.showOutletSelection(
          coverages,
          async (selectedOutlet: IDomainOutletCoverage) =>
            await proceedWithDelivery(
              selectedOutlet.id,
              location,
              selectedOutlet.state === OUTLET_STATES.PRE_ORDER_ONLY,
            ),
        );
        return;
      }

      if (coverages && coverages.length === 1) {
        outletId = coverages[0].id;
        isPreOrderOnly = coverages[0].state === OUTLET_STATES.PRE_ORDER_ONLY;
      } else {
        outletId = address.deliveryCoverage.outletId;
      }
    } catch {
      outletId = address.deliveryCoverage.outletId;
    }

    await proceedWithDelivery(outletId, location, isPreOrderOnly);
  };

  const proceedWithDelivery = async (
    outletId: string,
    location: Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">,
    isPreOrderOnly = false,
  ) => {
    try {
      isLoading.value = !modalHub.isOutletSelectionOn;

      if (isPreOrderOnly) {
        basket.setPendingDeliveryLocation(location);
      } else {
        await basket.loadBasketWithLocation({
          businessId: business.id,
          currency: business.currency,
          outletId,
          orderingMode: "delivery",
          salesChannel: business.salesChannel,
          location,
        });

        if (!customer.isLogged) {
          guest.removeAddress();
        }
      }

      const outlet = await outletRepo.getOutletById({
        outletId,
        salesChannel: business.salesChannel,
        orderingMode: "delivery",
      });

      await push("outlet", {
        orderingMode: "delivery",
        outletSlug: outlet.slug,
      });
    } catch (e) {
      isLoading.value = false;
      throw e;
    }
  };

  const getCoordinateCoverage = async (coordinate: IDomainCoordinate) => {
    try {
      return await businessRepo.getCoverageForCoordinates({
        businessId: business.id,
        salesChannel: business.salesChannel,
        lat: coordinate.lat,
        lng: coordinate.lng,
      });
    } catch {
      // silent error
    }
  };

  return {
    mapBoundaries,
    isLoading,
    deliveryAddress,
    deliveryLocation,
    currentGeoLocation,
    onDeliveryLocationSelected,
    onUncoveredDeliveryLocationSelected,
    onAddressSelected,
    onRefocusRegion,
    onDeliverTo,
    proceedWithDelivery,
    getCoordinateCoverage,
    setCurrentGeoLocation,
    setDeliveryLocationCoordinates,
  };
};
